/**
 * Slash commands the add-in answers itself, per host — not Cosmos skills. `/rephrase` (SC-06) runs the
 * local slide rephrase and `/summarise` (DI-01) the deck summary; neither has a slash of its own skill.
 *
 * AIVA-8971 adds `/shownotes` (DI-02) and `/slide` (DI-04). Each stands for a plain-language request its
 * local handler already answers ({@link localCommandRequest}), so a command and the same words typed out
 * run exactly the same code.
 */
import type { Channel } from "../../shared/host";
import { parseSlash } from "./parser";
import type { SlashCommand } from "./types";

export const REPHRASE_SLASH = "/rephrase";
export const SUMMARISE_SLASH = "/summarise";
export const SHOW_NOTES_SLASH = "/shownotes";
export const SLIDE_SLASH = "/slide";

const REPHRASE_COMMAND: SlashCommand = {
  slash: REPHRASE_SLASH,
  // Empty: there is no skill to force; useChat runs SC-06 locally, like the discovery slashes.
  skillKey: "",
  label: "Rephrase slide",
  description: "Rephrase the text on the current slide",
  hint: "how, e.g. tighten the bullets",
  prompt: "",
  category: "slide_content",
  autoApply: false,
};

const SUMMARISE_COMMAND: SlashCommand = {
  slash: SUMMARISE_SLASH,
  // Empty for the same reason: useChat reads the deck and runs DI-01 itself.
  skillKey: "",
  label: "Summarise deck",
  description: "Summarise the whole presentation in bullet points",
  hint: "how many bullets, e.g. 4 bullets",
  prompt: "",
  category: "deck",
  autoApply: false,
};

const SHOW_NOTES_COMMAND: SlashCommand = {
  slash: SHOW_NOTES_SLASH,
  // Empty for the same reason: useChat lists the notes locally (DI-02).
  skillKey: "",
  label: "Show speaker notes",
  description: "List speaker notes across slides",
  hint: "",
  prompt: "",
  category: "deck",
  autoApply: false,
};

const SLIDE_COMMAND: SlashCommand = {
  slash: SLIDE_SLASH,
  // Empty for the same reason: useChat reads the slide locally (DI-04).
  skillKey: "",
  label: "Slide contents",
  description: "Show what's on the current slide",
  hint: "",
  prompt: "",
  category: "deck",
  autoApply: false,
};

const LOCAL_COMMANDS: Readonly<Partial<Record<Channel, readonly SlashCommand[]>>> = {
  powerpoint: Object.freeze([REPHRASE_COMMAND, SUMMARISE_COMMAND, SHOW_NOTES_COMMAND, SLIDE_COMMAND]),
};

/**
 * The request `/shownotes` and `/slide` stand for — phrasings the local
 * matchers accept (matchListNotesIntent, isSlideContentRequest). Neither takes
 * anything typed after it. `/rephrase` and `/summarise` are read by their own
 * turns directly, so they are not here.
 */
const DECK_REQUESTS: Readonly<Record<string, () => string>> = {
  [SHOW_NOTES_SLASH]: () => "Show me the speaker notes",
  [SLIDE_SLASH]: () => "What's on this slide?",
};

export function getLocalCommandsForHost(host: Channel | null): readonly SlashCommand[] {
  return (host && LOCAL_COMMANDS[host]) || [];
}

/** True when `slash` is one of this host's local commands. */
export function isLocalCommand(host: Channel | null, slash: string): boolean {
  const normalized = slash.trim().toLowerCase();
  return getLocalCommandsForHost(host).some((cmd) => cmd.slash === normalized);
}

/**
 * The plain-language request a typed `/shownotes` or `/slide` stands for, or
 * null when `text` is neither (any other slash — `/rephrase` and `/summarise`
 * are read by their own turns — or another host).
 */
export function localCommandRequest(host: Channel | null, text: string): string | null {
  if (host !== "powerpoint") return null;
  const { token } = parseSlash(text);
  const toRequest = token ? DECK_REQUESTS[token] : undefined;
  return toRequest ? toRequest() : null;
}
